import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download, Euro, Gift, Package, RefreshCcw, ShieldAlert, ShoppingBag, Truck, CheckCircle2, Trash2, Star, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatCents = (c) => `€${(c / 100).toFixed(2).replace(".", ",")}`;
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const BADGE_STYLES = {
  paid: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30",
  completed: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30",
  active: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30",
  pending: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30",
  initiated: "bg-slate-700/40 text-slate-300 border-slate-600/50",
  nieuw: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/30",
  verzonden: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30",
  geleverd: "bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/30",
  expired: "bg-slate-700/40 text-slate-400 border-slate-600/50",
  failed: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30",
  canceled: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30",
  geannuleerd: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30",
  refunded: "bg-[#FF087B]/10 text-[#FF087B] border-[#FF087B]/30",
};

const StatusBadge = ({ status, testid }) => (
  <span
    data-testid={testid}
    className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono-brand uppercase tracking-wider border ${BADGE_STYLES[status] || BADGE_STYLES.initiated}`}
  >
    {status}
  </span>
);

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [trackInputs, setTrackInputs] = useState({});
  const [promoCode, setPromoCode] = useState("");
  const [promoPct, setPromoPct] = useState("10");
  const [promoBusy, setPromoBusy] = useState(false);
  const [returnFilter, setReturnFilter] = useState("open");

  const load = () => {
    axios
      .get(`${API}/admin/overview`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setError(true));
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
  }, [user]);

  const setFulfillment = async (orderId, status) => {
    setUpdating(orderId);
    try {
      await axios.patch(
        `${API}/admin/orders/${orderId}/status`,
        { status, tracking_code: trackInputs[orderId] || null },
        { withCredentials: true }
      );
      toast.success(
        status === "verzonden"
          ? `Bestelling ${orderId} verzonden — klant krijgt een e-mail`
          : `Bestelling ${orderId} is nu "${status}"`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUpdating(null);
    }
  };

  const decideReturn = async (orderId, decision) => {
    setUpdating(orderId);
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/return`, { decision }, { withCredentials: true });
      toast.success(
        decision === "approved"
          ? `Retour voor ${orderId} goedgekeurd — klant krijgt een e-mail`
          : `Retour voor ${orderId} afgewezen — klant krijgt een e-mail`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUpdating(null);
    }
  };

  const deleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review verwijderd");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    setPromoBusy(true);
    try {
      await axios.post(
        `${API}/admin/promos`,
        { code: promoCode.trim(), percent_off: parseInt(promoPct, 10) },
        { withCredentials: true }
      );
      toast.success(`Kortingscode ${promoCode.toUpperCase()} aangemaakt (${promoPct}%)`);
      setPromoCode("");
      setPromoPct("10");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPromoBusy(false);
    }
  };

  const deactivatePromo = async (code) => {
    try {
      await axios.delete(`${API}/admin/promos/${code}`, { withCredentials: true });
      toast.success(`Code ${code} gedeactiveerd`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
          <p className="font-mono-brand text-xs uppercase tracking-widest text-[#FF087B] mb-2">Beheer</p>
          <h1 className="font-display text-6xl sm:text-7xl uppercase tracking-wide text-white leading-none">
            Bestel<span className="text-gradient-splat">beheer</span>
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <a
              href="/brand/gelvaro-logo.pdf"
              download
              data-testid="admin-brand-pdf-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-[#00FF66] border border-slate-700 hover:border-[#00FF66]/50 rounded-full px-4 py-2 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Logo &amp; brandsheet (PDF)
            </a>
            <a
              href="/brand/gelvaro-profielfoto.pdf"
              download
              data-testid="admin-avatar-pdf-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-[#00E5FF] border border-slate-700 hover:border-[#00E5FF]/50 rounded-full px-4 py-2 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Profielfoto · donker (PDF)
            </a>
            <a
              href="/brand/gelvaro-profielfoto-rond.pdf"
              download
              data-testid="admin-avatar-round-pdf-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-[#00E5FF] border border-slate-700 hover:border-[#00E5FF]/50 rounded-full px-4 py-2 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Profielfoto · rond (PDF)
            </a>
            <a
              href="/brand/gelvaro-profielfoto-licht.pdf"
              download
              data-testid="admin-avatar-light-pdf-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-[#00E5FF] border border-slate-700 hover:border-[#00E5FF]/50 rounded-full px-4 py-2 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Profielfoto · licht (PDF)
            </a>
          </div>
        </motion.div>

        {user === null ? (
          <p className="text-slate-500" data-testid="admin-loading">Laden...</p>
        ) : !user || user.role !== "admin" || error ? (
          <div className="text-center py-20" data-testid="admin-no-access">
            <ShieldAlert className="w-14 h-14 text-[#FF087B] mx-auto mb-5" />
            <h2 className="font-display text-4xl uppercase tracking-wide text-white">Geen toegang</h2>
            <p className="mt-3 text-slate-400">Deze pagina is alleen voor beheerders. Log in met een beheerdersaccount.</p>
            <button
              data-testid="admin-login-link"
              onClick={() => navigate("/account")}
              className="mt-7 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase px-7 py-3.5 rounded-full glow-lime"
            >
              Naar inloggen
            </button>
          </div>
        ) : !data ? (
          <p className="text-slate-500" data-testid="admin-data-loading">Gegevens laden...</p>
        ) : (
          <div className="space-y-10">
            {(() => {
              const pendingReturns = data.orders.filter((o) => o.return_status === "requested");
              if (pendingReturns.length === 0) return null;
              return (
                <a
                  href="#admin-returns"
                  data-testid="admin-pending-returns-banner"
                  className="flex items-center justify-between gap-4 bg-[#FF087B]/10 border border-[#FF087B]/40 rounded-3xl px-6 py-4 hover:border-[#FF087B]/70 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Undo2 className="w-5 h-5 text-[#FF087B]" />
                    <p className="text-sm sm:text-base font-bold text-white">
                      {pendingReturns.length} openstaande {pendingReturns.length === 1 ? "retouraanvraag" : "retouraanvragen"} wacht op je beoordeling
                    </p>
                  </div>
                  <span className="shrink-0 bg-[#FF087B] text-white text-xs font-extrabold px-3 py-1.5 rounded-full" data-testid="admin-pending-returns-count">
                    {pendingReturns.length}
                  </span>
                </a>
              );
            })()}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
              {[
                { icon: Euro, label: "Omzet (betaald)", value: formatCents(data.stats.revenue_cents), color: "#00FF66", testid: "stat-revenue" },
                { icon: ShoppingBag, label: "Betaalde bestellingen", value: data.stats.orders_paid, color: "#00E5FF", testid: "stat-orders-paid" },
                { icon: RefreshCcw, label: "Actieve abonnementen", value: data.stats.subscriptions_active, color: "#FF087B", testid: "stat-subscriptions" },
                { icon: Package, label: "Totaal bestellingen", value: data.stats.orders_total, color: "#94A3B8", testid: "stat-orders-total" },
              ].map((s) => (
                <div key={s.label} className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6" data-testid={s.testid}>
                  <s.icon className="w-5 h-5 mb-3" style={{ color: s.color }} />
                  <p className="text-3xl font-extrabold text-white">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-bold">{s.label}</p>
                </div>
              ))}
            </div>

            <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8" data-testid="admin-orders-section">
              <h2 className="font-display text-3xl uppercase tracking-wide text-white mb-6">Bestellingen</h2>
              {data.orders.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="admin-orders-empty">Nog geen bestellingen.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-orders-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="pb-3 pr-4">Order</th>
                        <th className="pb-3 pr-4">Datum</th>
                        <th className="pb-3 pr-4">Klant</th>
                        <th className="pb-3 pr-4">Items</th>
                        <th className="pb-3 pr-4 text-right">Totaal</th>
                        <th className="pb-3 pr-4 text-right">Betaling</th>
                        <th className="pb-3 text-right">Verzending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.orders.map((o) => {
                        const fulfillment = o.fulfillment_status || "nieuw";
                        return (
                          <tr key={o.session_id} className="border-b border-slate-800/60" data-testid={`admin-order-row-${o.order_id}`}>
                            <td className="py-3.5 pr-4 font-mono-brand font-bold text-[#00E5FF] whitespace-nowrap align-top">{o.order_id}</td>
                            <td className="py-3.5 pr-4 text-slate-400 whitespace-nowrap align-top">{formatDate(o.created_at)}</td>
                            <td className="py-3.5 pr-4 align-top">
                              <p className="text-white font-semibold">{o.customer?.name}</p>
                              <p className="text-xs text-slate-500">{o.customer?.email} — {o.customer?.city}</p>
                            </td>
                            <td className="py-3.5 pr-4 text-slate-300 align-top">{o.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                            <td className="py-3.5 pr-4 text-right font-bold text-white whitespace-nowrap align-top">{formatCents(o.amount_total)}</td>
                            <td className="py-3.5 pr-4 text-right align-top"><StatusBadge status={o.payment_status} testid={`admin-order-status-${o.order_id}`} /></td>
                            <td className="py-3.5 text-right align-top">
                              <div className="flex items-center justify-end gap-2 flex-wrap">
                                <StatusBadge status={fulfillment} testid={`admin-fulfillment-${o.order_id}`} />
                                {fulfillment === "nieuw" && (
                                  <>
                                    <input
                                      data-testid={`admin-tracking-input-${o.order_id}`}
                                      value={trackInputs[o.order_id] || ""}
                                      onChange={(e) => setTrackInputs((t) => ({ ...t, [o.order_id]: e.target.value }))}
                                      placeholder="Track & trace (optioneel)"
                                      className="bg-slate-900/70 border border-slate-700 focus:border-[#00E5FF] rounded-full px-3 py-1.5 text-xs text-white placeholder:text-slate-500 outline-none w-44"
                                    />
                                    <button
                                      data-testid={`admin-ship-${o.order_id}`}
                                      onClick={() => setFulfillment(o.order_id, "verzonden")}
                                      disabled={updating === o.order_id}
                                      className="flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/20 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                                    >
                                      <Truck className="w-3.5 h-3.5" /> Verzonden
                                    </button>
                                  </>
                                )}
                                {fulfillment === "verzonden" && (
                                  <button
                                    data-testid={`admin-deliver-${o.order_id}`}
                                    onClick={() => setFulfillment(o.order_id, "geleverd")}
                                    disabled={updating === o.order_id}
                                    className="flex items-center gap-1.5 bg-slate-800 hover:bg-[#00FF66]/20 border border-slate-700 hover:border-[#00FF66]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Geleverd
                                  </button>
                                )}
                              </div>
                              {o.tracking_code && fulfillment !== "nieuw" && (
                                <p className="text-[11px] text-slate-500 mt-1.5" data-testid={`admin-tracking-${o.order_id}`}>
                                  T&T: {o.tracking_code}
                                </p>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section id="admin-returns" className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 scroll-mt-24" data-testid="admin-returns-section">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                <h2 className="font-display text-3xl uppercase tracking-wide text-white flex items-center gap-3">
                  <Undo2 className="w-7 h-7 text-[#FF087B]" /> Retouren
                </h2>
                <div className="flex bg-slate-900/70 border border-slate-800 rounded-full p-1" data-testid="admin-returns-filter">
                  {[["open", "Openstaand"], ["all", "Alle"]].map(([key, label]) => {
                    const count = key === "open"
                      ? data.orders.filter((o) => o.return_status === "requested").length
                      : data.orders.filter((o) => o.return_status).length;
                    return (
                      <button
                        key={key}
                        data-testid={`admin-returns-filter-${key}`}
                        onClick={() => setReturnFilter(key)}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                          returnFilter === key ? "bg-[#FF087B] text-white" : "text-slate-400 hover:text-white"
                        }`}
                      >
                        {label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-6">Keur retour- en annuleringsaanvragen van klanten goed of af. De klant krijgt automatisch een e-mail.</p>
              {(() => {
                const all = data.orders.filter((o) => o.return_status);
                const pending = all.filter((o) => o.return_status === "requested");
                const list = returnFilter === "open" ? pending : [...pending, ...all.filter((o) => o.return_status !== "requested")];
                if (list.length === 0) {
                  return <p className="text-sm text-slate-500" data-testid="admin-returns-empty">{returnFilter === "open" ? "Geen openstaande retouraanvragen." : "Nog geen retouraanvragen."}</p>;
                }
                return (
                  <ul className="space-y-3" data-testid="admin-returns-list">
                    {list.map((o) => (
                      <li key={o.order_id} className="bg-slate-800/40 rounded-2xl px-4 py-3" data-testid={`admin-return-row-${o.order_id}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-white flex items-center gap-2 flex-wrap">
                              <span className="font-mono-brand text-[#00E5FF]">{o.order_id}</span>
                              <StatusBadge
                                status={o.return_status === "requested" ? "pending" : o.return_status === "approved" ? "completed" : "canceled"}
                                testid={`admin-return-status-${o.order_id}`}
                              />
                              <span className="text-xs text-slate-500 font-normal">
                                {o.return_status === "requested" ? "Aangevraagd" : o.return_status === "approved" ? "Goedgekeurd" : "Afgewezen"}
                              </span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">{o.customer?.name} — {o.customer?.email} · {formatCents(o.amount_total)}</p>
                            {o.return_reason && <p className="text-xs text-slate-300 mt-1.5 italic">“{o.return_reason}”</p>}
                          </div>
                          {o.return_status === "requested" && (
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                data-testid={`admin-return-approve-${o.order_id}`}
                                onClick={() => decideReturn(o.order_id, "approved")}
                                disabled={updating === o.order_id}
                                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#00FF66]/20 border border-slate-700 hover:border-[#00FF66]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Goedkeuren
                              </button>
                              <button
                                data-testid={`admin-return-reject-${o.order_id}`}
                                onClick={() => decideReturn(o.order_id, "rejected")}
                                disabled={updating === o.order_id}
                                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/20 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                              >
                                <XCircle className="w-3.5 h-3.5" /> Afwijzen
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </section>

            <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8" data-testid="admin-subscriptions-section">
              <h2 className="font-display text-3xl uppercase tracking-wide text-white mb-6">Abonnementen</h2>
              {data.subscriptions.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="admin-subscriptions-empty">Nog geen abonnementen.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-subscriptions-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="pb-3 pr-4">Klant</th>
                        <th className="pb-3 pr-4">Abonnement</th>
                        <th className="pb-3 pr-4">Gestart</th>
                        <th className="pb-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.subscriptions.map((s) => (
                        <tr key={s.session_id} className="border-b border-slate-800/60" data-testid={`admin-sub-row-${s.session_id.slice(-8)}`}>
                          <td className="py-3.5 pr-4 text-white font-semibold">{s.email}</td>
                          <td className="py-3.5 pr-4 text-slate-300">{s.plan_name}</td>
                          <td className="py-3.5 pr-4 text-slate-400 whitespace-nowrap">{formatDate(s.created_at)}</td>
                          <td className="py-3.5 text-right"><StatusBadge status={s.status} testid={`admin-sub-status-${s.session_id.slice(-8)}`} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8" data-testid="admin-reviews-section">
              <h2 className="font-display text-3xl uppercase tracking-wide text-white mb-6">Reviews</h2>
              {data.reviews.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="admin-reviews-empty">Nog geen reviews geplaatst.</p>
              ) : (
                <ul className="space-y-3">
                  {data.reviews.map((r) => (
                    <li
                      key={r.review_id}
                      className="flex items-start justify-between gap-4 bg-slate-800/40 rounded-2xl px-4 py-3"
                      data-testid={`admin-review-row-${r.review_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-white flex items-center gap-2 flex-wrap">
                          {r.name}
                          <span className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "text-[#00FF66] fill-[#00FF66]" : "text-slate-700"}`} />
                            ))}
                          </span>
                        </p>
                        <p className="text-xs text-slate-400 mt-1">“{r.text}”</p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          {r.product ? `${r.product} — ` : ""}order {r.order_id}
                        </p>
                      </div>
                      <button
                        data-testid={`admin-review-delete-${r.review_id}`}
                        onClick={() => deleteReview(r.review_id)}
                        className="shrink-0 flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/20 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Verwijder
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8" data-testid="admin-promos-section">
              <h2 className="font-display text-3xl uppercase tracking-wide text-white mb-2">Kortingscodes</h2>
              <p className="text-xs text-slate-500 mb-6">Codes zijn direct actief in de checkout. Deactiveren kan op elk moment.</p>
              <form onSubmit={createPromo} className="flex flex-wrap items-end gap-3 mb-6" data-testid="promo-create-form">
                <label className="block">
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Code</span>
                  <input
                    data-testid="promo-code-input"
                    required
                    minLength={3}
                    maxLength={20}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    placeholder="SPLAT10"
                    className="bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] rounded-xl px-4 py-2.5 text-sm text-white uppercase placeholder:text-slate-500 outline-none w-44 font-mono-brand tracking-widest"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Korting (%)</span>
                  <input
                    data-testid="promo-percent-input"
                    required
                    type="number"
                    min={1}
                    max={90}
                    value={promoPct}
                    onChange={(e) => setPromoPct(e.target.value)}
                    className="bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] rounded-xl px-4 py-2.5 text-sm text-white outline-none w-28"
                  />
                </label>
                <button
                  type="submit"
                  data-testid="promo-create-button"
                  disabled={promoBusy}
                  className="bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold text-sm uppercase px-5 py-2.5 rounded-xl disabled:opacity-60"
                >
                  Aanmaken
                </button>
              </form>
              {!data.promos || data.promos.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="admin-promos-empty">Nog geen kortingscodes aangemaakt.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="admin-promo-stats-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] font-mono-brand uppercase tracking-wider text-slate-500 border-b border-slate-800">
                        <th className="pb-2 pr-4">Code</th>
                        <th className="pb-2 pr-4">Korting</th>
                        <th className="pb-2 pr-4 text-right">Gebruikt</th>
                        <th className="pb-2 pr-4 text-right">Omzet</th>
                        <th className="pb-2 pr-4 text-right">Korting gegeven</th>
                        <th className="pb-2 pr-4">Status</th>
                        <th className="pb-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.promos.map((p) => {
                        const s = data.promo_stats?.[p.code] || { uses: 0, revenue_cents: 0, discount_cents: 0 };
                        return (
                          <tr key={p.code} className="border-b border-slate-800/60" data-testid={`admin-promo-row-${p.code}`}>
                            <td className="py-3 pr-4 font-mono-brand font-bold text-[#00FF66] tracking-widest">{p.code}</td>
                            <td className="py-3 pr-4 text-slate-300">{p.percent_off}%</td>
                            <td className="py-3 pr-4 text-right text-white font-bold" data-testid={`admin-promo-uses-${p.code}`}>{s.uses}×</td>
                            <td className="py-3 pr-4 text-right text-white" data-testid={`admin-promo-revenue-${p.code}`}>{formatCents(s.revenue_cents)}</td>
                            <td className="py-3 pr-4 text-right text-[#FF087B]" data-testid={`admin-promo-discount-${p.code}`}>-{formatCents(s.discount_cents)}</td>
                            <td className="py-3 pr-4"><StatusBadge status={p.active ? "active" : "canceled"} testid={`admin-promo-status-${p.code}`} /></td>
                            <td className="py-3 text-right">
                              {p.active && (
                                <button
                                  data-testid={`admin-promo-deactivate-${p.code}`}
                                  onClick={() => deactivatePromo(p.code)}
                                  className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-[#FF087B]/20 border border-slate-700 hover:border-[#FF087B]/50 text-slate-200 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Deactiveer
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {data.bundle_stats && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-bundle-stats">
                  <div className="bg-slate-800/40 rounded-2xl px-4 py-3 col-span-2 sm:col-span-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-[#00E5FF] flex items-center gap-2"><Gift className="w-3.5 h-3.5" /> Automatische bundelkorting</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Vanaf 3 blasters €10 korting per blaster · bij 6 blasters gratis Bundel Pack</p>
                  </div>
                  <div className="bg-slate-800/40 rounded-2xl px-4 py-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider">Gebruikt</p>
                    <p className="text-lg font-extrabold text-white" data-testid="admin-bundle-uses">{data.bundle_stats.uses}×</p>
                  </div>
                  <div className="bg-slate-800/40 rounded-2xl px-4 py-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider">Omzet</p>
                    <p className="text-lg font-extrabold text-white" data-testid="admin-bundle-revenue">{formatCents(data.bundle_stats.revenue_cents)}</p>
                  </div>
                  <div className="bg-slate-800/40 rounded-2xl px-4 py-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider">Korting gegeven</p>
                    <p className="text-lg font-extrabold text-[#FF087B]" data-testid="admin-bundle-discount">-{formatCents(data.bundle_stats.discount_cents)}</p>
                  </div>
                  <div className="bg-slate-800/40 rounded-2xl px-4 py-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider">Gratis packs</p>
                    <p className="text-lg font-extrabold text-[#00E5FF]" data-testid="admin-bundle-gifts">{data.bundle_stats.gifts}</p>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
